import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from '../shared/services/api';
import { brand, primaryButtonSx } from '../theme';

interface NLPRule {
  id: string;
  rule_text: string;
  rule_name: string;
  rule_type: string;
  conditions: any[];
  actions: any[];
  priority: string;
  is_enabled: boolean;
  execution_count: string;
  success_count: string;
  failure_count: string;
  parsing_confidence: string;
  validation_status: string;
  created_at: string;
}

const RULE_CATEGORIES = [
  {
    label: 'Field Fill Rules',
    color: '#1976d2',
    examples: [
      '"Always use EORI DE123456789012 as declarant"',
      '"Set warehouse code 1234 for all textile imports"',
      '"Use procedure code 4000 for shipments from China to Germany via sea"',
    ],
  },
  {
    label: 'Validation Rules',
    color: '#388e3c',
    examples: [
      '"Block auto-submission if country of origin is missing"',
      '"Trigger compliance check for all dual-use goods"',
      '"Revoke auto-send when total value exceeds 10000 EUR"',
    ],
  },
  {
    label: 'Tariff / HS Code Rules',
    color: '#f57c00',
    examples: [
      '"Set HS code 61091000 for all cotton t-shirt line items"',
      '"Classify electronics with HS code 8471 when description contains laptop"',
      '"Apply tariff code 8544 for cable products"',
    ],
  },
];

const extractApiError = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'An error occurred';
  if (typeof detail === 'string') return detail;
  // detail is an object: { message, errors[] }
  const base = detail.message || 'Rule validation failed';
  const fieldErrors: string[] = (detail.errors || []).map((e: any) => e.message).filter(Boolean);
  return fieldErrors.length ? `${base}\n${fieldErrors.join('\n')}` : base;
};

const NLPRules: React.FC = () => {
  const [nlpRules, setNlpRules] = useState<NLPRule[]>([]);
  const [nlpLoading, setNlpLoading] = useState(false);
  const [openNlpDialog, setOpenNlpDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [ruleText, setRuleText] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [editingNlpRule, setEditingNlpRule] = useState<NLPRule | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchNlpRules();
  }, []);

  const fetchNlpRules = async () => {
    try {
      setNlpLoading(true);
      const res = await api.get('/api/v1/nlp/rules');
      setNlpRules(res.data.rules);
    } catch (err: any) {
      setError(extractApiError(err));
    } finally {
      setNlpLoading(false);
    }
  };

  const handleNlpPreview = async () => {
    if (!ruleText.trim()) { setError('Please enter a rule'); return; }
    try {
      setNlpLoading(true);
      const res = await api.post('/api/v1/nlp/rules/parse-preview', {
        rule_text: ruleText,
        rule_name: ruleName || null,
      });
      setPreviewData(res.data);
      setOpenPreviewDialog(true);
      setError('');
    } catch (err: any) {
      setError(extractApiError(err));
    } finally {
      setNlpLoading(false);
    }
  };

  const handleSaveNlpRule = async () => {
    if (!ruleText.trim()) { setError('Please enter a rule'); return; }
    try {
      setNlpLoading(true);
      if (editingNlpRule) {
        await api.put(`/api/v1/nlp/rules/${editingNlpRule.id}`, {
          rule_text: ruleText,
          rule_name: ruleName || null,
        });
        setSuccess('Rule updated successfully');
      } else {
        await api.post('/api/v1/nlp/rules', {
          rule_text: ruleText,
          rule_name: ruleName || null,
        });
        setSuccess('Rule created successfully');
      }
      setOpenNlpDialog(false);
      setOpenPreviewDialog(false);
      setRuleText('');
      setRuleName('');
      setEditingNlpRule(null);
      setPreviewData(null);
      fetchNlpRules();
    } catch (err: any) {
      setError(extractApiError(err));
    } finally {
      setNlpLoading(false);
    }
  };

  const handleToggleNlpRule = async (ruleId: string) => {
    try {
      await api.patch(`/api/v1/nlp/rules/${ruleId}/toggle`);
      fetchNlpRules();
    } catch (err: any) {
      setError(extractApiError(err));
    }
  };

  const handleDeleteNlpRule = async (ruleId: string) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await api.delete(`/api/v1/nlp/rules/${ruleId}`);
      fetchNlpRules();
      setSuccess('Rule deleted successfully');
    } catch (err: any) {
      setError(extractApiError(err));
    }
  };

  const handleEditNlpRule = (rule: NLPRule) => {
    setEditingNlpRule(rule);
    setRuleText(rule.rule_text);
    setRuleName(rule.rule_name);
    setOpenNlpDialog(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ color: brand.textPrimary }}>
          Automation Rules
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Describe your rules in plain English — the AI interprets and applies them automatically during document processing.
        </Typography>
      </Box>

      {/* ── Category hint card ──────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5 }}>
            Rules can belong to any of these three categories:
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {RULE_CATEGORIES.map((cat) => (
              <Box key={cat.label} sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ mb: 0.5, color: cat.color }}
                >
                  {cat.label}
                </Typography>
                {cat.examples.map((ex, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.78rem', lineHeight: 1.6 }}
                  >
                    {ex}
                  </Typography>
                ))}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingNlpRule(null); setRuleText(''); setRuleName(''); setOpenNlpDialog(true); }}
          sx={primaryButtonSx}
        >
          Create Rule
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rule Name</TableCell>
              <TableCell>Rule Text</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Executions</TableCell>
              <TableCell>Success Rate</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nlpLoading && nlpRules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : nlpRules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No rules yet. Click "Create Rule" and describe what you want in plain English.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              nlpRules.map((rule: NLPRule) => {
                const execCount = parseInt(rule.execution_count) || 0;
                const successCount = parseInt(rule.success_count) || 0;
                const successRate = execCount > 0 ? ((successCount / execCount) * 100).toFixed(0) : '0';
                return (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{rule.rule_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={rule.rule_text}>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rule.rule_text}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell><Chip label={rule.rule_type} size="small" /></TableCell>
                    <TableCell>
                      <Chip label={rule.priority} size="small" color={getPriorityColor(rule.priority) as any} />
                    </TableCell>
                    <TableCell>
                      <Switch checked={rule.is_enabled} onChange={() => handleToggleNlpRule(rule.id)} size="small" />
                    </TableCell>
                    <TableCell>{rule.execution_count}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={parseInt(successRate) > 80 ? 'success.main' : 'text.secondary'}>
                        {successRate}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => handleEditNlpRule(rule)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteNlpRule(rule.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Create / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={openNlpDialog} onClose={() => setOpenNlpDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingNlpRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Rule Name (Optional)"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              fullWidth
              placeholder="e.g., Default EORI for Exports"
            />
            <TextField
              label="Describe your rule in plain English"
              value={ruleText}
              onChange={(e) => setRuleText(e.target.value)}
              fullWidth
              multiline
              rows={4}
              placeholder='e.g., "Always use EORI DE123456789012 as declarant for all exports"'
              helperText="The AI will interpret your instruction and apply it automatically during processing"
            />
            <Alert severity="info">
              <Typography variant="body2" gutterBottom fontWeight="bold">Examples by category:</Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Fill:</Box>
                  {' "Set warehouse code 1234 for all textile imports"'}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 'bold', color: '#388e3c' }}>Validation:</Box>
                  {' "Block auto-submission if country of origin is missing"'}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 'bold', color: '#f57c00' }}>Tariff:</Box>
                  {' "Set HS code 61091000 for all cotton t-shirt line items"'}
                </Typography>
              </Stack>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNlpDialog(false)}>Cancel</Button>
          <Button onClick={handleNlpPreview} disabled={nlpLoading || !ruleText.trim()}>Preview</Button>
          <Button onClick={handleSaveNlpRule} variant="contained" disabled={nlpLoading || !ruleText.trim()} sx={primaryButtonSx}>
            {nlpLoading ? <CircularProgress size={24} /> : editingNlpRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Preview Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Rule Preview</DialogTitle>
        <DialogContent>
          {previewData && (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{previewData.parsed_rule.rule_name}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip label={previewData.parsed_rule.rule_type} size="small" />
                    <Chip label={previewData.parsed_rule.priority} size="small" color={getPriorityColor(previewData.parsed_rule.priority) as any} />
                    <Chip
                      label={`${(previewData.parsed_rule.confidence * 100).toFixed(0)}% confidence`}
                      size="small"
                      color={previewData.parsed_rule.confidence > 0.8 ? 'success' : 'warning'}
                    />
                  </Stack>
                  <Typography variant="subtitle2" gutterBottom>Conditions:</Typography>
                  {previewData.parsed_rule.conditions.map((cond: any, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ ml: 2 }}>• {cond.description}</Typography>
                  ))}
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Actions:</Typography>
                  {previewData.parsed_rule.actions.map((action: any, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ ml: 2 }}>• {action.description}</Typography>
                  ))}
                </CardContent>
              </Card>
              {previewData.validation.warnings.length > 0 && (
                <Alert severity="warning">
                  <Typography variant="body2" gutterBottom><strong>Warnings:</strong></Typography>
                  {previewData.validation.warnings.map((warn: any, idx: number) => (
                    <Typography key={idx} variant="body2">• {warn.message}</Typography>
                  ))}
                </Alert>
              )}
              {previewData.suggestions.length > 0 && (
                <Alert severity="info">
                  <Typography variant="body2" gutterBottom><strong>Suggestions:</strong></Typography>
                  {previewData.suggestions.map((sug: string, idx: number) => (
                    <Typography key={idx} variant="body2">• {sug}</Typography>
                  ))}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreviewDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveNlpRule} variant="contained" disabled={nlpLoading} sx={primaryButtonSx}>
            {nlpLoading ? <CircularProgress size={24} /> : 'Save Rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
};

export default NLPRules;
